import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { SlideshowSelectEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {import('./slideshow').Slideshow} slideshow - The slideshow component inside the dialog.
 * @property {HTMLButtonElement} [zoomInButton]
 * @property {HTMLButtonElement} [zoomOutButton]
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'slideshow'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    this.addEventListener(SlideshowSelectEvent.eventName, this.handleSlideSelect, { signal });
    this.addEventListener('drag-zoom:change', this.handleZoomChange, { signal });
    this.refs.dialog.addEventListener('wheel', this.handleDialogWheel, { passive: false, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #controller = new AbortController();

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, slideshow } = this.refs;
    const slides = slideshow.refs?.slides ?? slideshow.slides;
    const targetSlide = slides?.[index];

    const open = () => {
      dialog.showModal();
      // When called very early (before slideshow initializes), still open the dialog.
      if (typeof slideshow.select === 'function') {
        slideshow.select(index, undefined, { animate: false });
      }
      this.updateZoomControls();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetSlide) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetSlide.style.setProperty('view-transition-name', transitionName);
    });

    targetSlide.style.removeProperty('view-transition-name');

    this.#loadHighResolutionImage(targetSlide);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles slideshow selection inside the dialog.
   * @param {SlideshowSelectEvent} event
   */
  handleSlideSelect = debounce((event) => {
    const { index, previousIndex, slide } = event.detail;
    if (!(slide instanceof HTMLElement)) return;

    // Reset zoom on the slide we're leaving so returning starts at 1x.
    const previousWrapper = this.#getDragZoomWrapperByIndex(previousIndex);
    previousWrapper?.resetZoom();

    this.#loadHighResolutionImage(slide);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
    this.updateZoomControls();
  }, 50);

  /** @param {CustomEvent<{scale:number, atMin:boolean, atMax:boolean}>} _event */
  handleZoomChange = (_event) => {
    this.updateZoomControls();
  };

  /** @param {WheelEvent} event */
  handleDialogWheel = (event) => {
    // While the dialog is open, never let wheel scroll the page behind it.
    if (!this.refs.dialog?.open) return;
    event.preventDefault();
    event.stopPropagation();

    const wrapper = this.#getActiveDragZoomWrapper();
    if (!wrapper) return;

    wrapper.zoomByWheel(event.deltaY, event.clientX, event.clientY);
    this.updateZoomControls();
  };

  #getActiveDragZoomWrapper() {
    const { slideshow } = this.refs;
    const slides = slideshow.refs?.slides ?? slideshow.slides;
    const activeSlide = slides?.[slideshow.current];
    if (!(activeSlide instanceof HTMLElement)) return null;
    return /** @type {import('./drag-zoom-wrapper').DragZoomWrapper | null} */ (
      activeSlide.querySelector('drag-zoom-wrapper')
    );
  }

  /** @param {number} index */
  #getDragZoomWrapperByIndex(index) {
    const { slideshow } = this.refs;
    const slides = slideshow.refs?.slides ?? slideshow.slides;
    const targetSlide = slides?.[index];
    if (!(targetSlide instanceof HTMLElement)) return null;
    return /** @type {import('./drag-zoom-wrapper').DragZoomWrapper | null} */ (
      targetSlide.querySelector('drag-zoom-wrapper')
    );
  }

  /** @param {PointerEvent} event */
  zoomIn(event) {
    event.preventDefault();
    const wrapper = this.#getActiveDragZoomWrapper();
    wrapper?.zoomIn();
    this.updateZoomControls();
  }

  /** @param {PointerEvent} event */
  zoomOut(event) {
    event.preventDefault();
    const wrapper = this.#getActiveDragZoomWrapper();
    wrapper?.zoomOut();
    this.updateZoomControls();
  }

  updateZoomControls() {
    const zoomInButton = this.refs.zoomInButton;
    const zoomOutButton = this.refs.zoomOutButton;
    if (!(zoomInButton instanceof HTMLButtonElement) || !(zoomOutButton instanceof HTMLButtonElement)) return;

    const wrapper = this.#getActiveDragZoomWrapper();
    if (!wrapper) {
      zoomInButton.disabled = true;
      zoomOutButton.disabled = true;
      return;
    }

    const atMin = wrapper.scale <= wrapper.minZoom + 0.01;
    const atMax = wrapper.scale >= wrapper.maxZoom - 0.01;
    zoomOutButton.disabled = atMin;
    zoomInButton.disabled = atMax;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, slideshow } = this.refs;
    const slides = slideshow.refs?.slides ?? slideshow.slides;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = slideshow.current;
    const activeSlide = slides?.[activeIndex];
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide || !activeSlide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    // Wait for the thumbnails UI to finish animating if present
    await onAnimationEnd(dialog);

    activeSlide.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      activeSlide.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  // Thumbnail interactions are handled by slideshow-controls + slideshow-component.
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
